import { useQuery } from '@tanstack/react-query'
import { Copy, Download, ExternalLink } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { servicenowCmdbApi } from '../api'

interface ServiceNowUpdatesDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

/** One CSV cell. Quoted always, so a CI name with a comma or quote survives the change ticket. */
const csvCell = (v: string | null | undefined) => `"${String(v ?? '').replace(/"/g, '""')}"`

/**
 * What ServiceNow still has to be told, laid out for the customer's change team: which CI, which
 * field, what to enter (or clear). Nothing here is ticked off by hand — each row goes when the
 * next sync reads the right value back off the CI.
 */
export function ServiceNowUpdatesDialog({ open, onOpenChange }: ServiceNowUpdatesDialogProps) {
    const { toast } = useToast()
    const { data, isLoading } = useQuery({
        queryKey: ['cmdb-servicenow-updates'],
        queryFn: async () => (await servicenowCmdbApi.getServiceNowUpdates()).data?.data,
        enabled: open,
    })
    const updates = data?.updates ?? []
    const field = data?.field ?? 'u_vault_sentinel_id'

    const rowsAsText = () => updates
        .map((u) => [u.ciName ?? '', u.sysId, field, u.action === 'set' ? (u.value ?? '') : '(clear)'].join('\t'))
        .join('\n')

    const copyAll = async () => {
        await navigator.clipboard.writeText(`CI\tsys_id\tField\tSet to\n${rowsAsText()}`)
        toast({ title: 'Copied', description: `${updates.length} updates, ready to paste into a change ticket.` })
    }

    const exportCsv = () => {
        const lines = [
            ['CI', 'sys_id', 'Class', 'Table', 'Field', 'Set to'].map(csvCell).join(','),
            ...updates.map((u) => [u.ciName, u.sysId, u.ciClass, data?.table, field, u.action === 'set' ? u.value : ''].map(csvCell).join(',')),
        ]
        const url = URL.createObjectURL(new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' }))
        const a = document.createElement('a')
        a.href = url
        a.download = `servicenow-updates-${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    const ciLink = (sysId: string) => data?.instanceUrl
        ? `${data.instanceUrl.replace(/\/$/, '')}/nav_to.do?uri=${encodeURIComponent(`${data.table}.do?sys_id=${sysId}`)}`
        : null

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
                <DialogHeader>
                    <DialogTitle>ServiceNow updates</DialogTitle>
                    <DialogDescription>
                        {data?.mode === 'manual'
                            ? 'Your change team enters these in ServiceNow. The next sync reads each one back and it leaves this list.'
                            : 'ServiceNow refused these writes. Enter them in ServiceNow, or fix the integration account’s access and align again. The next sync reads each one back.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={copyAll} disabled={updates.length === 0}>
                        <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy all
                    </Button>
                    <Button size="sm" variant="outline" onClick={exportCsv} disabled={updates.length === 0}>
                        <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
                    </Button>
                </div>

                <div className="flex-1 min-h-0 overflow-auto rounded-md border">
                    {isLoading ? (
                        <p className="p-4 text-sm text-muted-foreground">Loading…</p>
                    ) : updates.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground">Nothing is waiting. ServiceNow holds the right Vault Sentinel ID on every aligned CI.</p>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-muted/60 text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left font-medium">CI</th>
                                    <th className="px-3 py-2 text-left font-medium">sys_id</th>
                                    <th className="px-3 py-2 text-left font-medium">Field</th>
                                    <th className="px-3 py-2 text-left font-medium">Set to</th>
                                    <th className="px-3 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {updates.map((u) => {
                                    const link = ciLink(u.sysId)
                                    return (
                                        <tr key={u.id}>
                                            <td className="px-3 py-2">
                                                <span className="font-medium">{u.ciName ?? '—'}</span>
                                                {u.ciClass && <span className="block text-xs text-muted-foreground">{u.ciClass}</span>}
                                            </td>
                                            <td className="px-3 py-2 font-mono text-xs select-all">{u.sysId}</td>
                                            <td className="px-3 py-2 font-mono text-xs">{field}</td>
                                            <td className="px-3 py-2 font-mono text-xs select-all">
                                                {u.action === 'set' ? (u.value ?? '—') : <span className="font-sans text-muted-foreground">clear it (left the MVC)</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                {link && (
                                                    <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                                                        Open <ExternalLink className="h-3 w-3" />
                                                    </a>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
